import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Lock, Mail } from 'lucide-react';
import okReservaLogo from '../../assets/okreserva-logo.png';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  useEffect(() => {
    if (localStorage.getItem('admin_token')) navigate('/admin/dashboard');
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('admin_token', data.access_token);
        localStorage.setItem('admin_user', JSON.stringify(data.user));
        toast.success('Bienvenido, Super Admin');
        navigate('/admin/dashboard');
      } else {
        toast.error(data.detail || 'Credenciales inválidas');
      }
    } catch (error) {
      toast.error('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: '#f4f6fa', minHeight: '100vh' }} className="flex">
      {/* Left panel — branding */}
      <div className="hidden lg:flex w-[420px] flex-shrink-0 flex-col" style={{ background: '#0f1117' }}>
        <div className="flex-1 flex flex-col items-center justify-center p-12 text-center">
          {/* Logo mark */}
          <div className="mb-6">
            <img
              src={okReservaLogo}
              alt="OkReserva"
              className="h-10 w-auto mx-auto"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </div>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.12em' }} className="uppercase mb-10">
            Super Admin Panel
          </p>

          {/* Feature pills */}
          <div className="space-y-3 w-full text-left">
            {[
              { icon: '🏪', label: 'Gestión de restaurantes' },
              { icon: '📊', label: 'Analíticas en tiempo real' },
              { icon: '🤖', label: 'Agentes IA integrados' },
              { icon: '🔌', label: 'Widgets y API pública' },
            ].map((f) => (
              <div key={f.label} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10 }}
                className="flex items-center gap-3 px-4 py-3">
                <span className="text-lg">{f.icon}</span>
                <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.875rem', fontWeight: 500 }}>{f.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="p-8 text-center">
          <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem' }}>
            © 2026 OkReserva. Todos los derechos reservados.
          </p>
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        {/* Mobile logo */}
        <div className="lg:hidden mb-10 text-center">
          <img
            src={okReservaLogo}
            alt="OkReserva"
            className="h-9 w-auto mx-auto mb-2"
          />
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">Super Admin</p>
        </div>

        {/* Card */}
        <div className="w-full max-w-md" style={{ background: '#ffffff', border: '1px solid #e8ecf0', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.07)' }}>
          <div className="p-8">
            <div className="mb-7">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Iniciar sesión</h2>
              <p className="text-sm text-gray-500">Ingresa tus credenciales de administrador</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Correo electrónico</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="admin@okreserva.com"
                    required
                    data-testid="admin-email-input"
                    style={{
                      width: '100%', height: 48, paddingLeft: 40, paddingRight: 16,
                      background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: 10,
                      fontSize: '0.9rem', color: '#111827', outline: 'none',
                      transition: 'border-color 0.15s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#f97316'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Contraseña</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    data-testid="admin-password-input"
                    style={{
                      width: '100%', height: 48, paddingLeft: 40, paddingRight: 16,
                      background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: 10,
                      fontSize: '0.9rem', color: '#111827', outline: 'none',
                      transition: 'border-color 0.15s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#f97316'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-btn"
                style={{
                  width: '100%', height: 48, marginTop: 4,
                  background: loading ? '#fdba74' : 'linear-gradient(135deg, #f97316, #ea6c10)',
                  color: '#ffffff', border: 'none', borderRadius: 10,
                  fontSize: '0.95rem', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 14px rgba(249,115,22,0.30)',
                  transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                }}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {loading ? 'Verificando...' : 'Acceder al Panel'}
              </button>
            </form>
          </div>

          {/* Card footer */}
          <div style={{ borderTop: '1px solid #f0f2f5', padding: '14px 32px', borderRadius: '0 0 16px 16px', background: '#fafbfc' }}>
            <p className="text-xs text-center text-gray-400">
              Acceso restringido · Solo personal autorizado
            </p>
          </div>
        </div>

        <p className="mt-8 text-xs text-gray-400">© 2026 OkReserva</p>
      </div>
    </div>
  );
}
